import './styles.scoped.css'
import {Typography, Avatar, Chip} from '@mui/material'
import {Check, Close, Image, InsertDriveFile} from '@mui/icons-material'
import {getColorFromName, getInitials, ignoreTimezone, formatTime} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton, ChipSkeleton, AvatarSkeleton} from '../../../../components'

const VisitInformationSkeleton = () => (
    <div className="card">
        <Typography variant="h8" color="text.primary">
            VISIT INFORMATION
        </Typography>
        <SkeletonContainer>
            <br />
            <br />
            <ChipSkeleton width={190} />
            <br />
            <br />
            <div className="content" style={{alignItems: 'start'}}>
                <Typography variant="h9" color="text.primary">
                    Schedule Time:
                </Typography>
                <div>
                    <TextSkeleton width={96} />
                    <TextSkeleton width={128} />
                </div>
            </div>
            <br />
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Total Service Rate:
                </Typography>
                <TextSkeleton width={48} />
                <Typography variant="body2" color="text.secondary">
                    |
                </Typography>
                <Typography variant="h9" color="text.primary">
                    Rate per Unit:
                </Typography>
                <TextSkeleton width={36} />
            </div>
            <br />
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Total Unit:
                </Typography>
                <TextSkeleton width={56} />
            </div>
            <br />
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Caregiver:
                </Typography>
                <div className="people-card">
                    <AvatarSkeleton size={24} />
                    <TextSkeleton width={110} />
                </div>
            </div>
            <br />
            <Typography variant="h9" color="text.primary">
                Service Activities:
            </Typography>
            {['30%', '22%', '26%'].map((width, index) => (
                <div key={index}>
                    <br />
                    <div className="activity-card">
                        <TextSkeleton width={width} fontSize={15} />
                        <TextSkeleton width="60%" />
                    </div>
                </div>
            ))}
        </SkeletonContainer>
    </div>
)

export default ({visit, skeleton = false}) => {
    if (skeleton) {
        return <VisitInformationSkeleton />
    }

    const completedCount = visit?.activities?.filter(item => item.status === 'completed')?.length || 0
    const totalActivities = visit?.activities?.length || 0

    return (
        <div className="card">
            <Typography variant="h8" color="text.primary">
                VISIT INFORMATION
            </Typography>
            <br />
            <br />
            <Chip label={`${completedCount} of ${totalActivities} activities completed`} size="small" style={{backgroundColor: '#0000000A'}} />
            <br />
            <br />
            <div className="content" style={{alignItems: 'start'}}>
                <Typography variant="h9" color="text.primary">
                    Schedule Time:
                </Typography>
                <div>
                    <Typography variant="body2" color="text.primary">
                        {format(new Date(ignoreTimezone(visit?.date)), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {formatTime(visit?.start_time)} - {formatTime(visit?.end_time)}
                    </Typography>
                </div>
            </div>
            <br />
            {visit?.clock_in_time && (
                <>
                    <div className="content" style={{alignItems: 'start'}}>
                        <Typography variant="h9" color="text.primary">
                            Visit Time:
                        </Typography>
                        <div>
                            <Typography variant="body2" color="text.primary">
                                {format(new Date(ignoreTimezone(visit?.clock_in_time ?? null)), 'MMM dd, yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {format(new Date(visit?.clock_in_time ?? null), 'h:mm a')} -{' '}
                                {format(new Date(visit?.clock_out_time ?? null), 'h:mm a')}
                            </Typography>
                        </div>
                    </div>
                    <br />
                </>
            )}
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Total Service Rate:
                </Typography>
                <Typography variant="body2" color="text.primary">
                    ${visit?.total_cost}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    |
                </Typography>
                <Typography variant="h9" color="text.primary">
                    Rate per Unit:
                </Typography>
                <Typography variant="body2" color="text.primary">
                    ${visit?.schedule?.service_agreement_item?.rate_per_unit}
                </Typography>
            </div>
            <br />
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Total Unit:
                </Typography>
                <Typography variant="body2" color="text.primary">
                    {visit?.total_units} units
                </Typography>
            </div>
            <br />
            <div className="content">
                <Typography variant="h9" color="text.primary">
                    Caregiver:
                </Typography>
                <div className="people-card">
                    {visit?.caregiver?.avatar_url ? (
                        <Avatar src={visit?.caregiver?.avatar_url} style={{width: 24, height: 24}} />
                    ) : (
                        <Avatar
                            style={{
                                backgroundColor: getColorFromName(visit?.caregiver?.first_name + ' ' + visit?.caregiver?.last_name),
                                width: 24,
                                height: 24,
                                fontSize: 12,
                            }}>
                            {getInitials(visit?.caregiver?.first_name + ' ' + visit?.caregiver?.last_name)}
                        </Avatar>
                    )}
                    <Typography variant="body2" color="text.primary">
                        {visit?.caregiver?.first_name + ' ' + visit?.caregiver?.last_name}
                    </Typography>
                </div>
            </div>
            <br />
            <Typography variant="h9" color="text.primary">
                Service Activities:
            </Typography>
            {visit?.activities?.map((item, index) => (
                <div key={index}>
                    <br />
                    <div className="activity-card">
                        <div className="content">
                            {item?.status === 'completed' && <Check color="primary" />}
                            {item?.status === 'cancelled' && <Close color="error" />}
                            <Typography variant="h9" color="primary">
                                {item?.activity_name}
                            </Typography>
                        </div>
                        <Typography variant="body2" color="text.primary">
                            {item?.instructions}
                        </Typography>
                        {item?.reason && (
                            <>
                                <Typography variant="body2" color="error">
                                    Reason:
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    {item?.reason}
                                </Typography>
                            </>
                        )}
                    </div>
                </div>
            ))}
            {visit?.visit_notes && (
                <>
                    <br />
                    <br />
                    <Typography variant="h9" color="text.primary">
                        Service Notes or Instructions:
                    </Typography>
                    <br />
                    <Typography variant="body2" color="text.primary">
                        {visit?.visit_notes}
                    </Typography>
                </>
            )}
            {visit?.incident_report && (
                <>
                    <br />
                    <br />
                    <Typography variant="h9" color="text.primary">
                        Incident:
                    </Typography>
                    <br />
                    <Typography variant="body2" color="text.primary">
                        {visit?.incident_report}
                    </Typography>
                </>
            )}
            {visit?.attachments?.length > 0 && (
                <>
                    <br />
                    <br />
                    <Typography variant="h9" color="text.primary">
                        Attachment:
                    </Typography>
                    <br />
                    <div className="attachments">
                        {visit.attachments.map((item, index) =>
                            item.type === 'file' ? (
                                <div className="attachment-file" key={index}>
                                    <InsertDriveFile />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.name}
                                    </Typography>
                                </div>
                            ) : (
                                <div className="attachment-thumb" key={index}>
                                    <Image fontSize="large" />
                                </div>
                            ),
                        )}
                    </div>
                </>
            )}
            <br />
            <br />
            <div className="location">
                {visit?.clock_in_latitude && visit?.clock_in_longitude && (
                    <div style={{flex: 1}}>
                        <Typography variant="h9" color="text.primary">
                            Clock-In Location:
                        </Typography>
                        <br />
                        <div className="location-map">
                            <iframe
                                src={`https://www.google.com/maps?q=${visit?.clock_in_latitude},${visit?.clock_in_longitude}&hl=es;z=14&output=embed`}
                                width="137"
                                height="100"
                                style={{border: 'none', borderRadius: 16}}
                            />
                            <Typography variant="body2" color="text.primary" style={{maxWidth: 150}}>
                                {visit?.clock_in_address}
                            </Typography>
                        </div>
                    </div>
                )}
                {visit?.clock_out_latitude && visit?.clock_out_longitude && (
                    <div style={{flex: 1}}>
                        <Typography variant="h9" color="text.primary">
                            Clock-Out Location:
                        </Typography>
                        <br />
                        <div className="location-map">
                            <iframe
                                src={`https://www.google.com/maps?q=${visit?.clock_out_latitude},${visit?.clock_out_longitude}&hl=es;z=14&output=embed`}
                                width="137"
                                height="100"
                                style={{border: 'none', borderRadius: 16}}
                            />
                            <Typography variant="body2" color="text.primary" style={{maxWidth: 150}}>
                                {visit?.clock_out_address}
                            </Typography>
                        </div>
                    </div>
                )}
            </div>
            {(visit?.caregiver_signature || visit?.client_signature) && (
                <>
                    <br />
                    <br />
                    <Typography variant="h9" color="text.primary">
                        Signature:
                    </Typography>
                    <br />
                    <br />
                    <div className="signature">
                        {visit?.caregiver_signature && (
                            <div className="signature-card">
                                <Typography variant="h9" color="primary">
                                    Caregiver's Signature
                                </Typography>
                                <br />
                                <br />
                                <div className="signature-box">
                                    <img src={visit?.caregiver_signature} height={182} />
                                </div>
                            </div>
                        )}
                        {visit?.client_signature && (
                            <div className="signature-card">
                                <Typography variant="h9" color="primary">
                                    Client's Signature
                                </Typography>
                                <br />
                                <br />
                                <div className="signature-box">
                                    <img src={visit?.client_signature} height={182} />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
